/**
 * Adapter: ListAppointmentItem → WorklistAppointmentCard props (PROD-1279).
 *
 * Faithful port of the field logic in Flutter `TodayAppointmentRowWidget`
 * (scrips_msp1_pa appointment_list_widget.dart:885-1179). Maps the rich worklist
 * fields the lean `toApptCard` drops.
 *
 * Status label (Flutter):
 *   - In-Progress bucket  → "Unsigned"
 *   - otherwise           → status.display
 *   (the nurse-signed "Triaged" override + vaccination stripe are metaData-gated
 *    and deferred — they need POST /api/Patients/AppointmentMetaData.)
 *
 * Modality (location-driven, like Flutter `locationIcon`):
 *   name ~ home → home · name ~ virtual|tele → virtual · else office
 *
 * Appointment kind: appointmentType.code === "FOLLOWUP" → follow-up, else first-time.
 */
import type {
  WorklistAppointmentCardProps,
  WorklistApptModality,
  WorklistApptKind,
} from '@scripsteam/scrips-signal-ds';
import type { StatusChipProps } from '@scripsteam/scrips-signal-ds';
import type {
  ListAppointmentItem,
  PatientResponse,
  AppointmentMetaData,
} from '@/types/appointment-api';
import { pmStatusLabel, pmStatusSemantic } from '@/lib/pm-appointment-status';
import { formatClockTime } from '@/shared/lib/time-format';

/** Flutter appointmentTypeFollowUp constant. */
const FOLLOW_UP_CODE = 'FOLLOWUP';

/** Statuses a nurse-signed appointment shows as "Triaged" (Flutter parity). */
const TRIAGED_STATUSES = new Set(['Booked', 'Arrived', 'CheckedIn']);

/** Card props minus the host-supplied interaction; carries the id for keying. */
export type WorklistCardData = { id: string } & Omit<WorklistAppointmentCardProps, 'onClick'>;

type Semantic = StatusChipProps['semantic'];

/**
 * Status colour: PM's palette (`@/lib/pm-appointment-status` → PM `StatusBadge`).
 *
 * WAS the DS canonical map (`@/lib/appointment-status`, Flutter color_helper truth — Samer reject
 * 2026-07-20, PROD-1588, which specifically restored Booked = GRAY over an info-blue map). Andrew
 * chose full PM parity for this surface on 2026-08-28 after being shown that exact conflict, so
 * Booked is blue again here. PROD-1588 needs amending by a human — see the header of
 * `pm-appointment-status.ts`; this comment is a record, not the amendment.
 */
function mapStatusSemantic(code: string): Semantic {
  return pmStatusSemantic(code);
}

function getPatient(item: ListAppointmentItem): PatientResponse | undefined {
  return item.participant.find((p) => p.patient)?.patient;
}

/** "{first} {middle} {last}" (Flutter name composition), blanks collapsed. */
function patientName(p: PatientResponse | undefined): string {
  if (!p) return 'Unknown Patient';
  if (p.name && p.name.trim()) return p.name;
  const parts = [p.firstName, p.middleName, p.lastName].filter((v) => v && v.trim());
  return parts.length ? parts.join(' ') : 'Unknown Patient';
}

function mapModality(item: ListAppointmentItem): WorklistApptModality {
  const loc = item.location?.name?.toLowerCase() ?? '';
  if (loc.includes('home')) return 'home';
  if (loc.includes('virtual') || loc.includes('tele')) return 'virtual';
  return 'office';
}

function mapKind(item: ListAppointmentItem): WorklistApptKind {
  return item.appointmentType?.code?.toUpperCase() === FOLLOW_UP_CODE ? 'follow-up' : 'first-time';
}

/** ISO instant → "9:30a" / "09:30", in the viewer's format (shared/lib/time-format.ts). */
export function formatApptTime(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return formatClockTime(d);
}

export interface ToWorklistCardOptions {
  /** When true, the status chip reads "Unsigned" (In-Progress bucket). */
  inProgress?: boolean;
  /**
   * Accepted for callers that still pass it (the ready list); it does not change the card. The label and
   * semantic are main's: an In-Progress card reads "Unsigned" whether or not its visit has ended.
   */
  ended?: boolean;
  /** Per-appointment encounter metadata (PROD-1280): drives Triaged + vaccination stripe. */
  meta?: AppointmentMetaData;
}

export function toWorklistCard(
  item: ListAppointmentItem,
  opts: ToWorklistCardOptions = {},
): WorklistCardData {
  const p = getPatient(item);
  const statusCode = item.status?.code ?? '';

  // Base status: "Unsigned" for In-Progress, else the display label. A nurse-
  // signed appointment in an active status reads "Triaged" — and that overrides
  // both (Flutter TodayAppointmentRowWidget order of precedence).
  // PM's naming for the ordinary case: the server `display` wins when present, except Fulfilled
  // which PM always surfaces as "Completed" (its API display is sometimes the raw "Fulfilled").
  // `Unsigned` / `Triaged` below are Flutter WORKLIST overrides, not appointment statuses — PM has
  // no equivalent surface, so they stay exactly as they were.
  let status = opts.inProgress
    ? 'Unsigned'
    : pmStatusLabel(statusCode, item.status?.display) || statusCode || 'N/A';
  let statusSemantic: Semantic = opts.inProgress ? 'warning' : mapStatusSemantic(statusCode);
  if (opts.meta?.isNurseSigned && TRIAGED_STATUSES.has(statusCode)) {
    status = 'Triaged';
    statusSemantic = 'success';
  }

  const importanceCode = p?.importance?.code?.trim() || undefined;

  return {
    id: item.id,
    patientName: patientName(p),
    photoUrl: p?.photoUrl ?? p?.photo ?? null,
    mrn: p?.mrn ?? undefined,
    age: p?.age ?? undefined,
    gender: p?.genderName ?? p?.gender ?? undefined,
    language: p?.languageDisplay ?? undefined,
    interpreterRequired: p?.interpreterRequired ?? undefined,
    importance: importanceCode,
    // Flutter getImportanceColor: VIP → lemon-green (success), else brand-blue (info).
    importanceSemantic: importanceCode === 'VIP' ? 'success' : 'info',
    status,
    statusSemantic,
    time: formatApptTime(item.start),
    room: item.room?.name ?? undefined,
    isRecurring: !!(item.recurringId && item.recurringId.trim()),
    modality: mapModality(item),
    reasonForVisit: item.reasonCode?.display ?? undefined,
    appointmentKind: mapKind(item),
    comment: item.comment ?? undefined,
    vaccinationPending: !!opts.meta?.vacar,
  };
}
